import { useState, useEffect } from "react";
import { NavLink, Navigate, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, ScrollText, Layers, BookMarked, Settings, LogOut, ExternalLink, Inbox, Crown, Users, ShieldCheck, Type, Menu, X, SquarePlus, LayoutTemplate, Mail, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { SquareLogo } from "@/components/ornaments";
import { useT } from "@/lib/labels";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/admin/articles", label: "Articles", icon: ScrollText, key: "articles" },
  { to: "/admin/submissions", label: "Reader Submissions", icon: Inbox, key: "submissions" },
  { to: "/admin/links", label: "Lore Links", icon: Link2, key: "links" },
  { to: "/admin/templates", label: "Article Templates", icon: LayoutTemplate, key: "templates" },
  { to: "/admin/sections", label: "Sections", icon: Layers, key: "sections" },
  { to: "/admin/series", label: "Series & Books", icon: BookMarked, key: "series" },
  { to: "/admin/vault", label: "Royal Vault", icon: Crown, key: "vault" },
  { to: "/admin/users", label: "Readers & Accounts", icon: Users, key: "users" },
  { to: "/admin/security", label: "Account & Security", icon: ShieldCheck, key: "security" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail, key: "newsletter" },
  { to: "/admin/wording", label: "Site Wording", icon: Type, key: "wording" },
  { to: "/admin/custom", label: "Custom Buttons & Headings", icon: SquarePlus, key: "custom" },
  { to: "/admin/settings", label: "Settings", icon: Settings, key: "settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSite();
  const t = useT();
  const [open, setOpen] = useState(false);
  useNoIndex();
  if (user === null) return <p className="p-20 text-center font-display text-2xl">Opening the study…</p>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/contribute" replace />;

  return (
    <div data-testid="admin-layout" className="min-h-screen flex flex-col md:flex-row">
      <aside className="md:w-64 md:min-h-screen bg-[#1a120c] text-[#eadfc4] flex flex-col shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
        <div className="flex items-center border-b border-[#c9a227]/30">
          <Link to="/" className="flex-1 min-w-0 flex items-center gap-3 p-4 md:p-5">
            <SquareLogo src={settings.logo_url} testid="admin-square-logo" />
            <span className="font-display text-lg leading-tight text-[#e6bb45]">{t("admin.side.title")}</span>
          </Link>
          <button type="button" data-testid="admin-mobile-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu"
            className="md:hidden mr-4 p-2 border border-[#c9a227]/60 text-[#e6bb45]">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
        <nav className={`${open ? "grid grid-cols-2" : "hidden"} md:flex md:flex-col md:flex-nowrap gap-1 p-3 flex-1`}>
          {LINKS.map(({ to, icon: Icon, end, key }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${key}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 font-heading text-[13px] tracking-wide max-md:px-3 max-md:text-[12px] md:whitespace-nowrap transition-colors ${isActive ? "bg-[#991b1b] text-[#fdf6e3] border-l-2 border-[#e6bb45]" : "hover:bg-white/5"}`}>
              <Icon className="w-4 h-4" /> {t(`admin.side.${key}`)}
            </NavLink>
          ))}
        </nav>
        <div className={`${open ? "flex" : "hidden"} md:flex p-3 border-t border-[#c9a227]/30 flex-wrap md:flex-col gap-1`}>
          <Link to="/" target="_blank" data-testid="admin-view-site" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#e6bb45] whitespace-nowrap"><ExternalLink className="w-4 h-4" /> {t("admin.side.view_site")}</Link>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#e6bb45] whitespace-nowrap"><LogOut className="w-4 h-4" /> {t("admin.side.sign_out")}</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-10"><Outlet /></main>
    </div>
  );
}

export const useNoIndex = () => useEffect(() => {
  const m = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" });
  document.head.appendChild(m);
  return () => m.remove();
}, []);

export const AdminTitle = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="font-display text-3xl sm:text-4xl mt-1">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);
